import type { ApiMessage } from '@xtreed/academy-shared';
import { api } from './api';

export interface Comment {
  id: string;
  content: string;
  userId: string;
  lessonId: string;
  parentId?: string | null;
  isEdited: boolean;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    avatar?: string | null;
    role: string;
  };
  _count?: {
    replies: number;
    reactions: number;
  };
}

export interface CommentsResponse {
  data: Comment[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateCommentData {
  content: string;
  lessonId: string;
  parentId?: string;
}

export interface UpdateCommentData {
  content: string;
}

export const commentsService = {
  async create(createData: CreateCommentData): Promise<Comment> {
    const { data } = await api.post('/comments', createData);
    return data?.data || data;
  },

  async findByLesson(
    lessonId: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<CommentsResponse> {
    const { data } = await api.get(`/comments/lessons/${lessonId}`, {
      params: { page, limit },
    });
    return data?.data || data;
  },

  async findReplies(commentId: string): Promise<Comment[]> {
    const { data } = await api.get(`/comments/${commentId}/replies`);
    return data?.data || data;
  },

  async update(
    commentId: string,
    updateData: UpdateCommentData,
  ): Promise<Comment> {
    const { data } = await api.put(`/comments/${commentId}`, updateData);
    return data?.data || data;
  },

  async delete(commentId: string): Promise<ApiMessage> {
    const { data } = await api.delete(`/comments/${commentId}`);
    return data?.data || data;
  },

  async togglePin(commentId: string): Promise<Comment> {
    const { data } = await api.post(`/comments/${commentId}/toggle-pin`);
    return data?.data || data;
  },
};
